import { AllVendorsListColumns } from "@/modules/vendors/constants/columns";
import { useGetVendorsListQuery } from "@/modules/vendors/requests";
import { VendorDetails } from "@/modules/vendors/types";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { usePagination } from "@/shared/hooks/usePagination";
import { useSearch } from "@/shared/hooks/useSearch";
import { useSort } from "@/shared/hooks/useSort";
import { Select, SelectItem, cn } from "@nextui-org/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useEffect, useState } from "react";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

interface AllThirdPartiesTabProps {
  onVendorCountChange?: (count: number, isLoading: boolean) => void;
}

export function AllThirdPartiesTab({ onVendorCountChange }: AllThirdPartiesTabProps) {
  const { data: allVendorsList, isFetching, isError } = useGetVendorsListQuery();
  const [selectedRisk, setSelectedRisk] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  const { paginationState, setPaginationState, resetPageNumber } = usePagination();

  const { searchTerm, setSearchTerm, filteredData: searchFilteredData } = useSearch<VendorDetails>(
    allVendorsList || [],
    {
      searchBy: (vendor) => [vendor.vendor_product_name],
      resetPageNumber,
    }
  );

  // Apply risk and status filters
  const filterByRiskAndStatus = (vendors: VendorDetails[]) => {
    return vendors.filter((vendor) => {
      const matchesRisk = selectedRisk === "all" || vendor.risk === selectedRisk;
      const matchesStatus = selectedStatus === "all" || vendor.status === selectedStatus;
      return matchesRisk && matchesStatus;
    });
  };

  const riskStatusFilteredData = filterByRiskAndStatus(searchFilteredData || []);

  const { currentPage, rowsPerPage } = paginationState;

  const totalVendors = riskStatusFilteredData?.length ?? 0;
  const totalPages = Math.ceil(totalVendors / rowsPerPage);

  const { sortedData, setSortModel } = useSort<VendorDetails>(
    riskStatusFilteredData || [],
    { column: "", direction: "ascending" },
    { paginationState }
  );

  const paginatedVendors =
    sortedData?.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage) ?? [];

  useEffect(() => {
    if (onVendorCountChange) {
      onVendorCountChange(allVendorsList?.length ?? 0, isFetching);
    }
  }, [allVendorsList, isFetching, onVendorCountChange]);

  return (
    <div className="h-full w-full flex flex-col gap-4 overflow-hidden mb-4">
      {/* Search and Filters */}
      <div className="flex items-center gap-4 mr-auto flex-shrink-0">
        <SearchField
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by vendor or product name"
        />

        <Select
          placeholder="Filter by Risk"
          selectedKeys={[selectedRisk]}
          onChange={(e) => {
            setSelectedRisk(e.target.value);
            resetPageNumber();
          }}
          className="w-[180px]"
        >
          <SelectItem key="all" value="all">
            All Risks
          </SelectItem>
          <SelectItem key="Low" value="Low">
            Low
          </SelectItem>
          <SelectItem key="Medium" value="Medium">
            Medium
          </SelectItem>
          <SelectItem key="High" value="High">
            High
          </SelectItem>
        </Select>

        <Select
          placeholder="Filter by Status"
          selectedKeys={[selectedStatus]}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            resetPageNumber();
          }}
          className="w-[180px]"
        >
          <SelectItem key="all" value="all">
            All Statuses
          </SelectItem>
          <SelectItem key="pending_review" value="pending_review">
            New Assessment
          </SelectItem>
          <SelectItem key="in_review" value="in_review">
            In Review
          </SelectItem>
          <SelectItem key="reviewed" value="reviewed">
            Reviewed
          </SelectItem>
        </Select>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 flex flex-col">
        <ZTableDeprecrated<VendorDetails>
          className={cn(
            "border-1 border-[#E4E4E7] rounded-lg overflow-auto scroll-auto flex-1"
          )}
          layout="fixed"
          loading
          rows={paginatedVendors || []}
          columns={AllVendorsListColumns}
          isHeaderSticky
          isCompact
          removeWrapper
          getRowId={(row) => row.uid}
          tableHeaderProps={{ className: "shadow-none rounded-none " }}
          tableRowProps={(row, rowIndex) => ({
            className: cn(rowIndex === 0 ? "border-none" : "border-y", "group"),
          })}
          classNames={{
            wrapper: "!shadow-none !rounded-none",
          }}
          aria-label="Vendors List"
          onSortModelChange={setSortModel}
          tableBodyProps={{
            emptyContent: isFetching ? (
              <div className="flex items-center justify-center">
                <LoadingSpinner
                  containerClassName="bg-transparent relative"
                  className="mx-auto"
                />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-gray-500 text-sm">No vendors found</p>
              </div>
            ) : (
              "No vendors found"
            ),
          }}
        />
        <ZTablePagination
          totalPages={totalPages}
          paginationState={paginationState}
          setPagination={setPaginationState}
        />
      </div>
    </div>
  );
}
